import { useCallback, useState } from 'react';
import { FormProvider, SubmitHandler } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { formatDateToISO } from '~/helpers/utils.ts';
import { errorMessages } from '~/constants/errorMessages.js';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader.tsx';
import { ApplicationStepContentRevoke } from '../components/ApplicationStepContentRevoke.tsx';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import SuccessModal from '~/components/modals/SuccessModal/SuccessModal.tsx';
import { useNavigate } from 'react-router';
import { useRetryAlert } from '../../hooks/useRetryAlert.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { RootState } from '~/store/store.ts';
import { FormDataRevoke } from '~/modules/processing-personal/PersonalDataApplicationForm/types.ts';
import { ApplicationSignatureRevoke } from '~/modules/processing-personal/PersonalDataApplicationForm/PersonalDataApplicationRevoke/ApplicationSignatureRevoke/ApplicationSignatureRevoke.tsx';
import { revokePostApplicationForm } from '~/store/processing-personal/thunks/applicationFormThunks.ts';
import { revokeDocumentPreviewPersonalData } from '~/store/processing-personal/thunks/documentPreviewPersonalThunks.ts';
import { useStepNavigation } from '~/components/StepNavigator/useStepNavigation.ts';
import { StepNavigator } from '~/components/StepNavigator/StepNavigator.tsx';
import { Box } from '@mui/material';
import { styles } from '~/modules/styles.ts';
import { TPersonalDataApplicationsSubmitForm } from '~/store/processing-personal/types.ts';
import { ApplicationRevokeFormValues } from '../types.ts';
import { useApplicationRevokeForm } from '../hooks/useApplicationRevokeForm.ts';
import { PROCESSING_PERSONAL_REVOKE_STEPS } from '~/modules/processing-personal/constants.ts';

export const ApplicationRevoke = () => {
  const dispatch = useAppDispatch();
  const { applicationId } = useSelector((state: RootState) => state.users.user.tokenData);
  const navigate = useNavigate();

  const { formData, loading: isLoading } = useSelector(
    (state: RootState) => state.processingPersonal
  );
  const normalizedFormData = formData as FormDataRevoke | undefined;

  const [current, setCurrent] = useState<number>(1);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isOpenModalCongratulations, setIsOpenModalCongratulations] = useState<boolean>(false);
  const [isModalCongratulationsText, setIsModalCongratulationsText] = useState<string | null>(null);

  const alert = useRetryAlert();
  const { next, items } = useStepNavigation(current, setCurrent, PROCESSING_PERSONAL_REVOKE_STEPS);

  const form = useApplicationRevokeForm(current, normalizedFormData);

  const handleCloseModal = (): void => {
    setIsModalOpen(false);
  };

  const closeModalCongratulations = (): void => {
    setIsOpenModalCongratulations(false);
    navigate('/');
  };

  const transformFormData = useCallback(
    (values: ApplicationRevokeFormValues): TPersonalDataApplicationsSubmitForm => {
      const { documentType, birthDate, issueDate, documentExpiryDate, ...rest } = values;

      return {
        ...rest,
        birthDate: formatDateToISO(birthDate),
        documentType: documentType?.value || null,
        issueDate: formatDateToISO(issueDate),
        documentExpiryDate: formatDateToISO(documentExpiryDate),
      };
    },
    []
  );

  const onSubmit: SubmitHandler<ApplicationRevokeFormValues> = async (values) => {
    if (isLoading) return;

    if (current === 1) {
      try {
        const dataForm = transformFormData(values);
        const res = await dispatch(
          revokePostApplicationForm({ appId: applicationId, dataForm })
        ).unwrap();

        if (res.applicationDocumentId) {
          try {
            await dispatch(
              revokeDocumentPreviewPersonalData({
                applicationId,
                applicationDocumentId: res.applicationDocumentId,
              })
            ).unwrap();
          } catch {
            alert.showError(errorMessages.SERVICE_UNAVAILABLE);
          }
        }

        next();
      } catch {
        alert.showError(errorMessages.SERVICE_UNAVAILABLE);
      }

      return;
    }
  };

  const handleRevokeApplication = (): void => {
    if (form.formState.isSubmitting) return;

    if (current === 2) {
      setIsModalOpen(true);
    }
  };

  const handleSignatureSuccess = (successMessage: string): void => {
    setIsOpenModalCongratulations(true);
    setIsModalCongratulationsText(successMessage);
    setIsModalOpen(false);
  };

  const openSignatureModal = (): void => {
    setIsModalOpen(true);
  };

  const handleSignatureError = (errorMessage: string, errorCode?: string): void => {
    setIsModalOpen(false);
    alert.handleRetryableError(errorMessage, errorCode, openSignatureModal);
  };

  const handleQrTimeout = (): void => {
    setIsModalOpen(false);
    alert.handleTimeout(openSignatureModal);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Box data-testid="applicationRevokeMain" sx={styles.mainFormPage}>
          <Box sx={styles.mainFormPageContainer}>
            <StepNavigator id={'stepsContainer'} current={current} items={items} />
            <Box
              component="main"
              data-testid="applicationRevokeContent"
              sx={styles.mainFormPageContent}
            >
              <ApplicationStepContentRevoke
                current={current}
                formData={normalizedFormData}
                isSubmitting={form.formState.isSubmitting || isModalOpen}
                onOptionSignature={handleRevokeApplication}
              />
            </Box>
          </Box>

          <ApplicationSignatureRevoke
            isModalOpen={isModalOpen}
            handleCloseModal={handleCloseModal}
            onSignatureSuccess={handleSignatureSuccess}
            onSignatureError={handleSignatureError}
            onQrTimeout={handleQrTimeout}
          />

          {isOpenModalCongratulations && (
            <SuccessModal
              isOpen={isOpenModalCongratulations}
              text={isModalCongratulationsText}
              onClose={closeModalCongratulations}
              showButton={true}
              onClick={closeModalCongratulations}
              buttonValue="Завершити"
              dataTestId="modalSuccess"
            />
          )}

          {alert.isOpen && (
            <ModalAlert
              isOpen={alert.isOpen}
              text={alert.text}
              onClose={alert.closeAlert}
              buttonValue={alert.retryAction ? 'Спробувати ще раз' : undefined}
              onClick={alert.retryAction ?? undefined}
              dataTestId="modalAlert"
            />
          )}

          {isLoading && <FullPageLoader />}
        </Box>
      </form>
    </FormProvider>
  );
};
